// ⚠ THIS MODULE IMPORTS NOTHING, so `recovery.test.ts` can run it under plain
// `node --experimental-strip-types` (the reason `fit.ts` gives).

/**
 * How many times the graph may be rebuilt inside `REBUILD_WINDOW_MS` before it stops and asks.
 *
 * ⚠⚠ A REBUILD IS A RECOVERY FROM A FAULT WE HAVE NOT FOUND, NOT A RETRY LOOP (app#349). A draw
 * that fails on the data the stores hold now fails again on the same data, so an unbounded rebuild
 * redraws, fails and redraws for as long as the tab is open. Three inside a minute covers a fault
 * that one bad moment caused (a wake, a refresh landing half-way), and it stops a fault that the
 * data causes every time. Past the budget the graph shows a control to draw it again.
 */
export const REBUILD_LIMIT = 3
export const REBUILD_WINDOW_MS = 60_000

/**
 * Decide whether one more rebuild is allowed at `now`, given the times of the earlier ones.
 * Returns the history to keep, with rebuilds older than the window removed and this one added
 * when it is allowed.
 */
export function allowRebuild(history: readonly number[], now: number): { allowed: boolean; history: number[] } {
  const recent = history.filter(at => now - at < REBUILD_WINDOW_MS)
  if (recent.length >= REBUILD_LIMIT) return { allowed: false, history: recent }
  return { allowed: true, history: [...recent, now] }
}

/**
 * Whether the graph needs a rebuild although nothing reported an error: the app has data to draw
 * and the SVG holds no drawing layer.
 *
 * ⚠ The drawing layer is the `g.zoom` group `viewport.ts` appends in the constructor, and
 * `destroy()` removes it. An SVG without it can draw nothing, whatever the stores hold. That is
 * the state a reload used to be the only way out of.
 */
export function drawingMissing(hasData: boolean, hasInstance: boolean, hasZoomLayer: boolean): boolean {
  if (!hasData) return false
  return !hasInstance || !hasZoomLayer
}
